import { ApplicationRoutes } from '~/types/RoutingTypes'
import { checkRole } from '~/utils/auth'
import { shouldUseHotelsExperience } from '~/utils/pbxUtils'

import { type HospitalityPermissions } from '@mangovoice/hotels-ui'
import type { Pbx, UserRoles } from '@mangovoice/types'

export type HotelsRouteAccess = 'dashboard' | 'settings' | 'wakeup' | 'history'

const hasInternalHotelsSettingsAccess = (userRoles?: UserRoles): boolean =>
  Boolean(userRoles?.is_staff || userRoles?.is_superuser)

export const NO_HOSPITALITY_PERMISSIONS: HospitalityPermissions = {
  canViewDashboard: false,
  canViewQueues: false,
  canEditQueues: false,
  canNewQueues: false,
  canDeleteQueues: false,
  canThumbDownloadQueues: false,
  canViewScheduler: false,
  canEditScheduler: false,
  canViewHistory: false,
  canViewSettings: false,
  canEditSettings: false,
  canThumbDownloadSettings: false,
}

/**
 * Web-app gating matrix:
 * - Dashboard: all authenticated users
 * - Wakeup scheduling: portal-unrestricted or higher
 * - Wakeup history: admin or higher
 * - Hotels settings: admin or internal users can edit reset schedule.
 *   Activation controls are not exposed in the web app.
 */
export const deriveHotelsPermissionsFromUserRoles = (
  userRoles?: UserRoles,
): HospitalityPermissions => {
  if (!userRoles) {
    return NO_HOSPITALITY_PERMISSIONS
  }

  const canViewDashboard = true
  const canManageWakeups = checkRole(userRoles, 'unrestricted')
  const canViewHistory = checkRole(userRoles, 'admin')
  const canManageResetSchedule =
    canViewHistory || hasInternalHotelsSettingsAccess(userRoles)
  const canResetAllRooms = hasInternalHotelsSettingsAccess(userRoles)

  return {
    canViewDashboard,
    canViewQueues: canViewDashboard,
    canEditQueues: canManageWakeups,
    canNewQueues: canManageWakeups,
    canDeleteQueues: canManageWakeups,
    canThumbDownloadQueues: canResetAllRooms,
    canViewScheduler: canManageWakeups,
    canEditScheduler: canManageWakeups,
    canViewHistory,
    canViewSettings: canManageResetSchedule,
    canEditSettings: false,
    canThumbDownloadSettings: canManageResetSchedule,
  }
}

export const canAccessHotelsRoute = (
  permissions: HospitalityPermissions,
  requiredAccess: HotelsRouteAccess,
): boolean => {
  if (requiredAccess === 'dashboard') {
    return Boolean(permissions.canViewDashboard)
  }

  if (requiredAccess === 'wakeup') {
    return Boolean(permissions.canViewScheduler)
  }

  if (requiredAccess === 'settings') {
    return Boolean(permissions.canViewSettings)
  }

  return Boolean(permissions.canViewHistory)
}

export const getFirstAllowedHotelsRoute = (
  permissions: HospitalityPermissions,
): ApplicationRoutes | null => {
  if (permissions.canViewDashboard) {
    return ApplicationRoutes.HOTELS
  }

  if (permissions.canViewSettings) {
    return ApplicationRoutes.HOTELS_SETTINGS
  }

  if (permissions.canViewScheduler) {
    return ApplicationRoutes.HOTELS_WAKEUP
  }

  if (permissions.canViewHistory) {
    return ApplicationRoutes.HOTELS_WAKEUP_HISTORY
  }

  return null
}

export const hasAnyHotelsAccess = (
  permissions: HospitalityPermissions,
): boolean => {
  return Boolean(getFirstAllowedHotelsRoute(permissions))
}

interface ResolveHotelsAccessRedirectParams {
  pbx?: Pick<Pbx, 'industry_type'> | null
  requiredAccess: HotelsRouteAccess
  permissions: HospitalityPermissions
  currentPathname: string
}

export const resolveHotelsAccessRedirect = ({
  pbx,
  requiredAccess,
  permissions,
  currentPathname,
}: ResolveHotelsAccessRedirectParams): ApplicationRoutes | null => {
  if (!shouldUseHotelsExperience({ pbx })) {
    return ApplicationRoutes.CALLS
  }

  if (canAccessHotelsRoute(permissions, requiredAccess)) {
    return null
  }

  const fallbackRoute = getFirstAllowedHotelsRoute(permissions)
  if (!fallbackRoute) {
    return ApplicationRoutes.CALLS
  }

  if (fallbackRoute === currentPathname) {
    return ApplicationRoutes.CALLS
  }

  return fallbackRoute
}
